/**
 * OrderRow — one table row for the desktop orders list.
 * Reads directly from BE shape — no adapter needed.
 *
 * Cột "Tổng tiền" dùng billableTotal — cùng con số với "Tổng thanh toán" ở màn
 * chi tiết: payable_total (đã gộp VAT gói) + phát sinh (đã gộp VAT dòng,
 * HRS-053). order.total là giá gói GỐC (chưa trừ giảm giá, chưa VAT, không
 * phát sinh) nên không phải số khách phải trả.
 */

import { useNavigate } from "@tanstack/react-router";
import { StaffAvatar } from "@/components/StaffAvatar";
import { OrderStatusBadge } from "./OrderStatusBadge";
import type { OrderSummary } from "../api";
import { OrderScoreBadge } from "@/features/performance/components/OrderScoreBadge";
import type { OrderScore } from "@/features/performance/api";
import { OrderAttentionBadges, type AttentionFlags } from "./OrderAttentionBadges";
import { formatVNDCompact } from "@/lib/format";
import { billableTotal } from "@/features/payments/lib";
import { useDataVisibility } from "@/lib/authv2/permissions";
import { SaleOrSquad } from "@/features/sales-squad/SaleOrSquad";
import { useBranchLabel } from "@/lib/branches";
import { useProductLabel } from "@/features/products/productLabelContext";
import { presetByLabel } from "../tags";
import { customerAvatar, customerChips } from "../customerCell";
import { ReshootChip } from "@/features/schedule/components/ReshootChip";

interface Props {
  /** Nhãn điểm của đơn. Vắng mặt (chưa chấm / không có quyền xem) là bình thường. */
  score?: OrderScore | undefined;
  order: OrderSummary;
  /** Ba nhãn nhắc (spec 4.4). Không có thì hàng vẽ y như trước. */
  flags?: AttentionFlags | undefined;
}

export function OrderRow({ order, flags, score }: Props) {
  const productLabel = useProductLabel();
  const branchLabel = useBranchLabel();
  const navigate = useNavigate();
  // QĐ 0132: cùng cờ với đầu bảng (OrderTable) — ba ô tiền + dòng SĐT chỉ vẽ cho người được xem.
  const { orderMoney, customerContact } = useDataVisibility();
  const orderTags = order.tags ?? [];
  const shootDateDisplay = order.shootDate
    ? new Date(order.shootDate).toLocaleDateString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

  return (
    <tr
      className="cursor-pointer border-b transition-colors hover:bg-primary/10"
      onClick={() => navigate({ to: "/orders/$orderId", params: { orderId: order.id } })}
    >
      {/* Không còn ô mã đơn — tên khách là thứ nhân viên nhận ra, mã máy thì
          không. Cả hàng vẫn bấm được để mở đơn (onClick ở <tr>). */}
      <td className="px-4 py-3 text-sm">
        <div className="flex items-start gap-2">
          {customerAvatar(order.customer, order.customerAvatarUrl)}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{order.customer || "—"}</span>
              {customerChips(order)}
              {/* Đơn bù 0đ — cùng dấu với ô lịch, để không ai tưởng đơn nhập thiếu tiền. */}
              {order.isReshoot && <ReshootChip />}
              <OrderAttentionBadges flags={flags} />
              {orderTags.map((label) => {
                const preset = presetByLabel(label);
                if (!preset) return null;
                return (
                  <span
                    key={label}
                    className="shrink-0 rounded px-1.5 py-px text-[10px] font-semibold"
                    style={{
                      background: preset.bg,
                      color: preset.color,
                      border: `1px solid ${preset.color}25`,
                    }}
                    title={preset.label}
                  >
                    {preset.label}
                  </span>
                );
              })}
            </div>
            {(order.saleName || order.branch) && (
              <div className="text-xs text-muted-foreground">
                {order.saleName ? (
                  <SaleOrSquad orderId={order.id} size={16} className="w-full">
                    <span className="inline-flex items-center gap-1 align-middle">
                      <StaffAvatar name={order.saleName} sizePx={16} />
                      {order.saleName}
                    </span>
                  </SaleOrSquad>
                ) : null}
                {order.saleName && branchLabel(order.branch) ? " • " : ""}
                {branchLabel(order.branch)}
              </div>
            )}
            {customerContact && order.phone && (
              <div className="text-xs text-muted-foreground">{order.phone}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm">{productLabel(order.product, { full: true }) || "—"}</td>
      <td className="px-4 py-3 text-sm">{branchLabel(order.branch) || "—"}</td>
      <td className="px-4 py-3 text-sm">{shootDateDisplay}</td>
      {orderMoney && (
        <>
          <td className="px-4 py-3 text-sm text-right">{formatVNDCompact(billableTotal(order))}</td>
          <td className="px-4 py-3 text-sm text-right">
            <div>{formatVNDCompact(order.deposit)}</div>
            {order.depositBillImage ? (
              <a
                href={order.depositBillImage}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[11px] text-emerald-700 underline-offset-2 hover:underline"
              >
                🧾 Bill cọc
              </a>
            ) : null}
          </td>
          <td className="px-4 py-3 text-sm text-right">
            {order.costumeExtra > 0 ? (
              <span className="text-primary">{formatVNDCompact(order.costumeExtra)}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </td>
        </>
      )}
      <td className="px-4 py-3">
        <OrderStatusBadge status={order.status} />
        <OrderScoreBadge score={score} />
      </td>
    </tr>
  );
}
